import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useUser, useClerk, useAuth as useClerkAuth } from "@clerk/nextjs";
import { useDatabaseConnection } from "./DatabaseContext";
import { useRouter } from "next/router";
import {
  ROLES,
  isAdmin as checkIsAdmin,
  isApprovedAgent as checkIsApprovedAgent,
  isPendingAgent as checkIsPendingAgent,
  isAnyAgent as checkIsAnyAgent,
} from "../lib/role-management";

// Create context
const AuthContext = createContext();

// Cache configuration
const USER_CACHE_CONFIG = {
  storageKey: "td_user_cache",
  ttl: 600000, // 10 minutes
};

// Auth provider component
export function AuthProvider({ children }) {
  const router = useRouter();
  const { user, isSignedIn, isLoaded } = useUser();
  const { userId } = useClerkAuth();
  const { signOut } = useClerk();
  const [dbUser, setDbUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSynced, setLastSynced] = useState(null);
  const { isConnected } = useDatabaseConnection();
  const [syncAttempts, setSyncAttempts] = useState(0);
  const MAX_SYNC_ATTEMPTS = 3;

  // Check if we should use cached user data
  const checkCachedUser = useCallback(() => {
    if (typeof window === "undefined") return null;

    try {
      const cachedData = localStorage.getItem(USER_CACHE_CONFIG.storageKey);
      if (!cachedData) return null;

      const cached = JSON.parse(cachedData);

      // Validate cache freshness and user match
      if (
        cached &&
        cached.updatedAt &&
        cached.clerkId === user?.id &&
        Date.now() - new Date(cached.updatedAt).getTime() <
          USER_CACHE_CONFIG.ttl
      ) {
        console.log("Using valid cached user data");
        return cached;
      }
    } catch (e) {
      console.error("Error parsing cached user data:", e);
      // Clear invalid cache
      localStorage.removeItem(USER_CACHE_CONFIG.storageKey);
    }

    return null;
  }, [user?.id]);

  // Save user data to cache
  const updateCachedUser = useCallback((userData) => {
    if (typeof window === "undefined" || !userData) return;

    try {
      const cacheData = {
        ...userData,
        updatedAt: new Date().toISOString(),
      };

      localStorage.setItem(
        USER_CACHE_CONFIG.storageKey,
        JSON.stringify(cacheData)
      );

      // Also store user role in session storage for push notification filtering
      if (userData.role) {
        sessionStorage.setItem("user-role", userData.role);
        localStorage.setItem("user-role", userData.role);
      }

      // Store user ID for push notification filtering
      if (userData.clerkId) {
        sessionStorage.setItem("clerk-user-id", userData.clerkId);
        localStorage.setItem("current-user-id", userData.clerkId);
      }
    } catch (e) {
      console.error("Error caching user data:", e);
    }
  }, []);

  // Create robust fallback user from Clerk data
  const createFallbackUser = useCallback(() => {
    if (!user) return null;

    // Safely get Clerk metadata with defaults
    const metadata = user.publicMetadata || {};
    const safeMetadata = {
      role: typeof metadata.role === "string" ? metadata.role : ROLES.USER,
      approved:
        typeof metadata.approved === "boolean" ? metadata.approved : true,
    };

    // Get primary email safely
    let email = "";
    try {
      email =
        user.primaryEmailAddress?.emailAddress ||
        user.emailAddresses?.[0]?.emailAddress ||
        `user-${user.id.substring(0, 8)}@fallback.com`;
    } catch (e) {
      email = `user-${user.id.substring(0, 8)}@fallback.com`;
    }

    console.log("Creating robust fallback user with role:", safeMetadata.role);

    return {
      clerkId: user.id,
      firstName: user.firstName || "User",
      lastName: user.lastName || "",
      email,
      profileImage: user.imageUrl || "/default-avatar.png",
      role: safeMetadata.role,
      approved: safeMetadata.approved,
      isFallback: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  }, [user]);

  // Handle sign out with cleanup
  const handleSignOut = useCallback(async () => {
    try {
      // Clear local user data
      if (typeof window !== "undefined") {
        localStorage.removeItem(USER_CACHE_CONFIG.storageKey);
      }

      setDbUser(null);
      setError(null);

      // Sign out via Clerk
      await signOut();

      // Redirect to home page
      router.push("/");
    } catch (e) {
      console.error("Error during sign out:", e);
    }
  }, [signOut, router]);

  // Sync Clerk user with database user
  const syncUserData = useCallback(
    async (force = false) => {
      // Don't sync if not signed in or Clerk hasn't loaded
      if (!isSignedIn || !isLoaded) {
        setDbUser(null);
        setIsLoading(false);
        setError(null);
        return;
      }

      // Check cache first unless forced refresh
      if (!force) {
        const cachedUser = checkCachedUser();
        if (cachedUser) {
          setDbUser(cachedUser);
          setLastSynced(new Date(cachedUser.updatedAt));
          setIsLoading(false);
          return;
        }
      }

      // Start loading state
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch("/api/users/sync", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        // Handle HTTP errors with detailed info
        if (!response.ok) {
          let errorDetails = "";

          try {
            const errorData = await response.json();
            errorDetails = errorData.error || errorData.message || "No details";
          } catch {
            errorDetails = response.statusText;
          }

          throw new Error(`HTTP ${response.status}: ${errorDetails}`);
        }

        const data = await response.json();
        if (!data?.success || !data?.user) {
          throw new Error(data?.error || "Invalid API response structure");
        }

        // Success path - update user data and cache it
        const userData = data.user;
        setDbUser(userData);
        setLastSynced(new Date());
        updateCachedUser(userData);
        setSyncAttempts(0);
        setError(null);
      } catch (err) {
        console.error(`User sync failed:`, err.message);

        // Create a robust fallback user
        const fallbackUser = createFallbackUser();

        // Detailed error classification
        if (err.name === "AbortError") {
          setError({
            type: "timeout",
            message: "Connection timed out. Using local data.",
            blocking: false,
          });
        }
        // Handle server errors specifically
        else if (
          err.message.includes("500") ||
          err.message.includes("Internal Server Error")
        ) {
          setError({
            type: "server",
            message: "Server error during sync. Using local data.",
            blocking: false,
          });
        } else if (err.message.includes("401")) {
          setError({
            type: "auth",
            message: "Authentication issue detected.",
            blocking: false,
          });
        } else {
          // Generic error that doesn't block UI
          setError({
            type: "sync",
            message: "Background sync failed. Using available data.",
            blocking: false,
          });
        }

        // Always use fallback data to ensure UI access
        if (fallbackUser) {
          setDbUser(fallbackUser);
          updateCachedUser(fallbackUser);
          console.log("Using fallback user data to maintain dashboard access");
        }

        setSyncAttempts((prev) => prev + 1);
      } finally {
        setIsLoading(false);
        setLastSynced(new Date());
      }
    },
    [
      isSignedIn,
      isLoaded,
      checkCachedUser,
      updateCachedUser,
      createFallbackUser,
    ]
  );

  // Initial data sync when auth state changes
  useEffect(() => {
    // Only sync when Clerk is loaded and we know about DB connection
    if (isLoaded && isConnected !== undefined) {
      syncUserData();
    }
  }, [isSignedIn, isLoaded, isConnected, syncUserData]);

  // Derived auth state
  const isAuthenticated = !!isSignedIn;
  const isAdmin = checkIsAdmin(dbUser);
  const isAgent = checkIsAnyAgent(dbUser);
  const isApprovedAgent = checkIsApprovedAgent(dbUser);
  const isPendingAgent = checkIsPendingAgent(dbUser);
  const hasError = !!error;

  // Provide all auth values
  const authContextValue = {
    // User state
    user,
    dbUser,
    isAuthenticated,

    // Role helpers
    isAdmin,
    isAgent,
    isApprovedAgent,
    isPendingAgent,

    // Status information
    isLoading,
    hasError,
    error,
    lastSynced,
    syncAttempts,

    // Actions
    syncUserData,
    signOut: handleSignOut,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
